/**
 * Position API service for ATS.
 */

import { createApiClient } from '@aaas/shared';
import type {
  Position,
  PositionListResponse,
  PositionResponse,
  PositionCreateInput,
  PositionUpdateInput,
} from '../types';

interface ListPositionsOptions {
  page?: number;
  size?: number;
  sortBy?: string;
  order?: 'asc' | 'desc';
  search?: string;
  status?: string;
}

class PositionService {
  /**
   * List positions with pagination.
   */
  async listPositions(
    workspaceId: string,
    idToken: string,
    options: ListPositionsOptions = {}
  ): Promise<PositionListResponse> {
    const { page = 1, size = 10, sortBy = 'created_at', order = 'desc', search, status } = options;
    const skip = (page - 1) * size;

    const client = createApiClient(idToken, 'ats');
    const response = await client.get<PositionListResponse>(`/positions`, {
      params: { skip, limit: size, sort_by: sortBy, order, search, status },
      headers: { 'X-Workspace-ID': workspaceId },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to list positions');
    }

    return response.data;
  }

  /**
   * Get a single position by ID.
   */
  async getPosition(positionId: string, workspaceId: string, idToken: string): Promise<Position> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.get<PositionResponse>(`/positions/${positionId}`, {
      headers: { 'X-Workspace-ID': workspaceId },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to get position');
    }

    return response.data.data;
  }

  /**
   * Create a new position.
   */
  async createPosition(
    workspaceId: string,
    idToken: string,
    data: PositionCreateInput
  ): Promise<Position> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.post<PositionResponse>(`/positions`, data, {
      headers: { 'X-Workspace-ID': workspaceId },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to create position');
    }

    return response.data.data;
  }

  /**
   * Update a position.
   */
  async updatePosition(
    positionId: string,
    workspaceId: string,
    idToken: string,
    data: PositionUpdateInput
  ): Promise<Position> {
    const client = createApiClient(idToken, 'ats');
    const response = await client.patch<PositionResponse>(`/positions/${positionId}`, data, {
      headers: { 'X-Workspace-ID': workspaceId },
    });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update position');
    }

    return response.data.data;
  }

  /**
   * Delete a position.
   */
  async deletePosition(positionId: string, workspaceId: string, idToken: string): Promise<void> {
    const client = createApiClient(idToken, 'ats');
    await client.delete(`/positions/${positionId}`, {
      headers: { 'X-Workspace-ID': workspaceId },
    });
  }
}

const positionService = new PositionService();
export default positionService;
